import { computed, inject, Type } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, pipe, switchMap, throwError } from 'rxjs';
import { CrudService } from '../services/crud.service';
import { BaseEntity, EnhancedMessage } from '../models/base-entity';
import { SocketService } from '../services/socket.service';
import { SocketEvent } from '../models/socket-event';
import { ConfirmationDialogService } from '../services/confirmation-dialog.service';

/**
 * Best-effort human-readable label for an entity, used to give history
 * log entries a description (e.g. "Grundschulen" instead of just "Thema").
 */
function extractEntityLabel(item: any): string | undefined {
  return item?.title ?? item?.name ?? item?.username ?? undefined;
}

export interface StateFilter {
  query: string;
  tags: string[] | undefined;
  products: string[] | undefined;
  published: boolean | null;
  locked: boolean | null;
  category: string[];
  ids: string[] | undefined;
}

export type BaseState<Entity> = {
  items: { [key: string]: Entity };
  loading: boolean;
  initialized: boolean;
  itemsLoaded: boolean;
  selectedId: string;
  selection: Array<Entity>;
  version: number;
  lastPersistedId: string;
  lastPersistedLabel?: string;
  entityType: string;
  entityLabel: string;
  action?: string;
  filter: StateFilter;
  events?: any;
  trigger: any;
  triggerOptions?: any;
  error?: any;
};

export function withCrudOperations<Entity extends BaseEntity>(
  dataServiceType: Type<CrudService<Entity>>
) {
  return signalStoreFeature(
    {
      state: type<BaseState<Entity>>(),
    },
    withMethods(
      (
        store,
        socketService = inject(SocketService),
        confirmationDialogService = inject(ConfirmationDialogService)
      ) => {
        const service = inject(dataServiceType);

        return {
          /**
           * adds an entity to the store
           */
          addItem: rxMethod<Entity>(
            pipe(
              switchMap(item => {
                patchState(store, { error: undefined });
                return service.addItem(item).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: addedItem => {
                      if (addedItem._id) {
                        const updatedItems = {
                          ...store.items(),
                          [addedItem._id]: addedItem,
                        };

                        const addTrigger = store.events
                          ? store.events().add
                          : undefined;

                        patchState(store, {
                          lastPersistedId: addedItem._id,
                          lastPersistedLabel: extractEntityLabel(addedItem),
                          items: updatedItems,
                          selectedId: addedItem._id,
                          action: 'add',
                          trigger: addTrigger,
                        });

                        // messaging
                        if (item.message) {
                          if (!item.message.avoid) {
                            confirmationDialogService.customMessage(
                              item.message
                            );
                          }
                        } else {
                          confirmationDialogService.addMessage(
                            addedItem,
                            store.entityType()
                          );
                        }

                        // emit socket event afet adding item
                        socketService.emitEvent('baseEvent', {
                          action: 'add',
                          body: addedItem,
                          commune: 'dev',
                          type: service.instanceType,
                          trigger: store.events
                            ? store.events().socket
                            : undefined,
                        });
                      }
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        triggerOptions: undefined,
                        trigger: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          selectedId: undefined,
                          triggerOptions: undefined,
                          // NOT cleared here: this microtask runs before
                          // Angular's next change-detection pass, so
                          // clearing `error` immediately after the `error`
                          // callback set it means any template bound to
                          // store.error() never gets a chance to render it.
                          loading: false,
                          trigger: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),

          /**
           * load all commune-items
           */
          async loadAllItemsByPromise() {
            patchState(store, { loading: true, error: undefined });

            try {
              const items = await service.getItemsAsPromise();
              patchState(store, {
                items,
                loading: false,
                initialized: true,
                itemsLoaded: true,
              });
              setTimeout(() => {
                patchState(store, {
                  itemsLoaded: false,
                });
              }, 1);
            } catch (error) {
              // Without this, a rejected promise here leaves `loading: true`
              // and `initialized: false` forever - any UI gated on
              // store.isInitialized() (spinners, empty-state screens) hangs
              // indefinitely with no error surfaced.
              patchState(store, { loading: false, error });
            }
          },

          /**
           * load by ids
           */
          async fetchById(id: string) {
            try {
              const items: Record<string, Entity> = {};
              const item = await service.getItem(id);
              items[id] = item;
              patchState(store, {
                items,
                loading: false,
                initialized: true,
                itemsLoaded: true,
              });
              return item;
            } catch (error) {
              patchState(store, { loading: false, error });
              return undefined;
            }
          },

          /**
           * load by ids
           */
          async fetchByIds(ids: string[]) {
            patchState(store, { loading: true, error: undefined });

            try {
              const items = await service.getItemsByIds(ids);
              patchState(store, {
                items,
                loading: false,
                initialized: true,
                itemsLoaded: true,
              });
              setTimeout(() => {
                patchState(store, {
                  itemsLoaded: false,
                });
              }, 1);
            } catch (error) {
              patchState(store, { loading: false, error });
            }
          },

          overwriteItems(items: { [key: string]: Entity }) {
            patchState(store, {
              items,
              loading: false,
              initialized: true,
              itemsLoaded: true,
            });
          },

          /**
           * deletes one item from the store
           */
          deleteItem: rxMethod<Entity>(
            pipe(
              switchMap(item => {
                patchState(store, { loading: true, error: undefined });

                return service.removeItem(item).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: () => {
                      const itemId = item._id;
                      if (itemId) {
                        const updatedItems = { ...store.items() };
                        delete updatedItems[itemId];

                        patchState(store, {
                          items: updatedItems,
                          action: 'delete',
                          lastPersistedId: item._id,
                          lastPersistedLabel: extractEntityLabel(item),
                          selectedId: undefined,
                          triggerOptions: { removedIds: [itemId] },
                          trigger: store.events
                            ? store.events().delete
                            : undefined,
                        });
                      }

                      // messaging
                      if (item.message) {
                        if (!item.message.avoid) {
                          confirmationDialogService.customMessage(item.message);
                        }
                      } else {
                        confirmationDialogService.removeMessage(
                          item,
                          store.entityType()
                        );
                      }

                      // emit socket event afet deleting item
                      socketService.emitEvent('baseEvent', {
                        action: 'delete',
                        commune: 'dev',
                        body: item,
                        type: service.instanceType,
                        trigger: store.events
                          ? store.events().delete
                          : undefined,
                      });
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        triggerOptions: undefined,
                        trigger: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          //selectedId: undefined,
                          //lastPersistedId: undefined,
                          // `error` intentionally not cleared here - see
                          // addItem's finalize for why.
                          loading: false,
                          trigger: undefined,
                          triggerOptions: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),

          /**
           * Persists a partial change to the backend and refreshes the cached
           * item, without setting `action`/`lastPersistedId` - unlike
           * `update()`, this does NOT count as "the form was submitted" for
           * `BaseFormComponent`'s navigate-away-on-submit effect. For side
           * effects that need to persist immediately without the user having
           * explicitly submitted the form (e.g. removing an uploaded file
           * right away rather than waiting for the next save).
           */
          patchItem: rxMethod<Entity>(
            pipe(
              switchMap(item => {
                return service.updateItem(item).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: () => {
                      if (item._id) {
                        patchState(store, {
                          items: { ...store.items(), [item._id]: item },
                        });
                      }
                    },
                    error: (error: any) => {
                      patchState(store, { error });
                    },
                  })
                );
              })
            )
          ),

          /**
           * updates one item in the store
           */
          update: rxMethod<Entity>(
            pipe(
              switchMap(item => {
                patchState(store, { error: undefined });
                return service.updateItem(item).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: response => {
                      if (item._id) {
                        const updatedItems = {
                          ...store.items(),
                          [item._id]: item,
                        };

                        patchState(store, {
                          items: updatedItems, // Update the items in the store
                          action: 'update',
                          lastPersistedId: item._id,
                          lastPersistedLabel: extractEntityLabel(item),
                          trigger: store.events
                            ? store.events().update
                            : undefined,
                        });

                        // messaging
                        if (item.message) {
                          if (!item.message.avoid) {
                            confirmationDialogService.customMessage(
                              item.message
                            );
                          }
                        } else {
                          confirmationDialogService.updateMessage(
                            item,
                            store.entityType()
                          );
                        }

                        // emit socket event after update item
                        socketService.emitEvent('baseEvent', {
                          action: 'update',
                          commune: 'dev',
                          body: item,
                          type: service.instanceType,
                          trigger: store.events
                            ? store.events().socket
                            : undefined,
                        });
                      }
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        trigger: undefined,
                        triggerOptions: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          //selectedId: undefined,
                          //lastPersistedId: undefined,
                          // `error` intentionally not cleared here - see
                          // addItem's finalize for why.
                          loading: false,
                          trigger: undefined,
                          triggerOptions: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),

          updateMultiple: rxMethod<{
            items: Entity[];
            message?: EnhancedMessage;
          }>(
            pipe(
              switchMap(({ items, message }) => {
                patchState(store, { error: undefined });
                return service.updateMultiple(items).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: response => {
                      const lastId = Object.keys(response)[0] || '';
                      const updatedItems = { ...store.items(), ...response };

                      patchState(store, {
                        items: updatedItems,
                        action: 'persistMultiple',
                        lastPersistedId: lastId,
                        lastPersistedLabel: extractEntityLabel(
                          (response as any)[lastId]
                        ),
                        trigger: store.events
                          ? store.events().update
                          : undefined,
                      });

                      // messaging
                      if (message) {
                        if (!message.avoid) {
                          confirmationDialogService.customMessage(message);
                        }
                      } else {
                        confirmationDialogService.updateMultipleMessage(
                          store.entityType()
                        );
                      }

                      // emit socket event after update item
                      socketService.emitEvent('baseEvent', {
                        action: 'persistMultiple',
                        commune: 'dev',
                        body: response,
                        type: service.instanceType,
                        trigger: store.events
                          ? store.events().socket
                          : undefined,
                      });
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        triggerOptions: undefined,
                        trigger: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          //selectedId: undefined,
                          //lastPersistedId: undefined,
                          triggerOptions: undefined,
                          // `error` intentionally not cleared here - see
                          // addItem's finalize for why.
                          loading: false,
                          trigger: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),
          insertMultiple: rxMethod<{
            items: Entity[];
            message?: EnhancedMessage;
          }>(
            pipe(
              switchMap(({ items, message }) => {
                patchState(store, { error: undefined });
                return service.insertMultiple(items).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: response => {
                      const updatedItems = { ...store.items(), ...response };
                      const lastId = Object.values(response)[0]?._id ?? '';

                      patchState(store, {
                        items: updatedItems,
                        action: 'persistMultiple',
                        lastPersistedId: lastId,
                        lastPersistedLabel: extractEntityLabel(
                          Object.values(response)[0]
                        ),
                        trigger:
                          store.events && store.events()
                            ? store.events().update
                            : undefined,
                      });

                      // messaging
                      if (message) {
                        if (!message.avoid) {
                          confirmationDialogService.customMessage(message);
                        }
                      } else {
                        confirmationDialogService.updateMultipleMessage(
                          store.entityType()
                        );
                      }

                      // emit socket event after update item
                      socketService.emitEvent('baseEvent', {
                        action: 'persistMultiple',
                        commune: 'dev',
                        body: items,
                        type: service.instanceType,
                        trigger: store.events
                          ? store.events().socket
                          : undefined,
                      });
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        trigger: undefined,
                        triggerOptions: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          //selectedId: undefined,
                          //lastPersistedId: undefined,
                          triggerOptions: undefined,
                          // `error` intentionally not cleared here - see
                          // addItem's finalize for why.
                          loading: false,
                          trigger: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),
          removeMultiple: rxMethod<{
            items: Entity[];
            message?: EnhancedMessage;
          }>(
            pipe(
              switchMap(({ items, message }) => {
                patchState(store, { error: undefined });
                return service.removeMultiple(items).pipe(
                  catchError((error: any) => {
                    return throwError(() => error);
                  }),
                  tapResponse({
                    next: response => {
                      let lastId = '';
                      let lastLabel: string | undefined;
                      const updatedItems = { ...store.items() };
                      const idsToRemove =
                        response || items.map(item => item._id);
                      idsToRemove.forEach(id => {
                        if (id) {
                          lastId = id;
                          lastLabel = extractEntityLabel(updatedItems[id]);
                        }
                        delete updatedItems[id];
                      });

                      patchState(store, {
                        items: updatedItems,
                        action: 'removeMultiple',
                        lastPersistedId: lastId,
                        lastPersistedLabel: lastLabel,
                        triggerOptions: { removedIds: [idsToRemove] },
                        trigger: store.events
                          ? store.events().delete
                          : undefined,
                      });

                      // messaging
                      if (message) {
                        if (!message.avoid) {
                          confirmationDialogService.customMessage(message);
                        }
                      } else {
                        confirmationDialogService.removeMultipleMessage(
                          store.entityType()
                        );
                      }

                      // emit socket event after update item
                      socketService.emitEvent('baseEvent', {
                        action: 'removeMultiple',
                        commune: 'dev',
                        body: items,
                        type: service.instanceType,
                        trigger: store.events
                          ? store.events().socket
                          : undefined,
                      });
                    },
                    error: (err: any) => {
                      patchState(store, {
                        action: undefined,
                        error: err,
                        lastPersistedId: undefined,
                        trigger: undefined,
                        triggerOptions: undefined,
                      });
                    },
                    finalize: () => {
                      queueMicrotask(() => {
                        patchState(store, {
                          action: undefined,
                          selectedId: undefined,
                          //lastPersistedId: undefined,
                          triggerOptions: undefined,
                          // `error` intentionally not cleared here - see
                          // addItem's finalize for why.
                          loading: false,
                          trigger: undefined,
                        });
                      });
                    },
                  })
                );
              })
            )
          ),

          /**
           * deselect one store-item
           */
          deselect(): void {
            patchState(store, () => ({
              selectedId: undefined,
              error: undefined,
            }));
          },

          /**
           * select one store-item
           * @param id
           */
          singleSelection(id: string): void {
            patchState(store, () => ({
              selectedId: id,
            }));
          },

          /**
           * Selects multiple entities based on the provided IDs, updates the current selection, and returns the selected entities.
           *
           * @param {string[]} ids - An array of unique identifiers representing the entities to select.
           * @return {Entity[]} An array of selected entities that correspond to the provided IDs.
           */
          selectMultiple(ids: string[]): Entity[] {
            const items = this.getByIds(ids);
            this.updateSelection(items);
            return items;
          },

          /**
           * select the oldest store-item
           */
          selectLatest(): void {
            patchState(store, () => ({
              selectedId: Object.values(store.items()).at(0)?._id || undefined,
            }));
          },

          /**
           * select the newest store-item
           */
          selectLast(): void {
            const all = Object.values(store.items());
            const lastIndex = all.length - 1;
            patchState(store, () => ({
              selectedId: all.at(lastIndex)?._id || undefined,
            }));
          },

          /**
           * update selection
           * multiple items
           * @param items
           */
          updateSelection(items: Array<Entity>): void {
            patchState(store, () => ({
              selection: items,
            }));
          },

          /**
           * clears selection
           */
          clearSelection(): void {
            patchState(store, () => ({
              selection: [],
            }));
          },

          /**
           * check if item is in store-selection
           * @param item
           */
          isInSelection(item: Entity): boolean {
            return store.selection().indexOf(item) > -1;
          },

          /**
           * returns one store-item by id
           * @param id
           */
          has(id: string): boolean {
            return !!store.items()[id];
          },

          /**
           * returns one store-item by id
           * @param id
           */
          getById(id: string): Entity {
            return store.items()[id] as Entity;
          },

          /**
           * returns many store-items by ids
           * @param ids
           */
          getByIds(ids: string[]) {
            const items: Entity[] = [];
            ids.forEach(id => {
              items.push(store.items()[id]);
            });
            return items.filter(item => !!item);
          },

          /**
           * update filter
           * @param filter
           */
          updateFilter(filter: Partial<StateFilter>): void {
            patchState(store, {
              filter: {
                ...{
                  query: '',
                  tags: undefined,
                  products: undefined,
                  published: null,
                  locked: null,
                  category: null,
                  ids: undefined,
                },
                ...store.filter(),
                ...filter,
              },
            });
          },

          /**
           * clear filter
           */
          clearFilter(): void {
            patchState(store, {
              filter: {
                query: '',
                tags: undefined,
                products: undefined,
                published: null,
                locked: null,
                category: [],
                ids: undefined,
              },
            });
          },

          clearTrigger(): void {
            patchState(store, {
              trigger: undefined,
              triggerOptions: undefined,
            });
          },

          async finishEmpty() {
            patchState(store, {
              items: {},
              loading: false,
              initialized: true,
              itemsLoaded: true,
            });
          },

          /**
           * initiates the items between different users
           */
          syncWithSocket: () => {
            /**
             * next todos:
             * check user ID before sync (no auth yet)
             * refactor curd.state.ts (this mess here)
             */
            socketService
              .onEvent('baseEvent')
              .subscribe((data: SocketEvent) => {
                if (
                  data.commune === 'dev' &&
                  data.type === service.instanceType &&
                  data.body
                ) {
                  if (data.body._id && data.action === 'delete') {
                    const itemId = data.body._id;
                    const { [itemId]: value, ...updatedItems } = store.items();
                    patchState(store, {
                      items: updatedItems,
                      trigger: data.trigger,
                    });
                  } else if (data.body && data.action === 'persistMultiple') {
                    const updatedItems = { ...store.items() };
                    data.body.forEach((item: Entity) => {
                      if (item._id) updatedItems[item._id] = item;
                    });
                    patchState(store, {
                      items: updatedItems,
                      version: store.version() + 1,
                      trigger: data.trigger,
                    });
                  } else if (data.body) {
                    const newItems = {
                      ...store.items(),
                      [data.body._id]: data.body,
                    };
                    patchState(store, {
                      items: newItems,
                      version: store.version() + 1,
                      trigger: data.trigger,
                    });
                  }
                }
              });
          },
        };
      }
    ),

    withComputed(({ selectedId, items, initialized, selection }) => ({
      allItems: computed(() => items()),
      selectedItem: computed(() => items()[selectedId()] as Entity),
      isInitialized: computed(() => initialized()),
      asArray: computed(() => Object.values(items())),
      allIds: computed(() => Object.keys(items())),
      count: computed(() => Object.keys(items()).length),
      unselectedItems: computed(() => {
        const selectedIds = selection().map(item => item._id);
        return Object.values(items()).filter(
          item => !selectedIds.includes(item._id)
        );
      }),
      tagsInUse: computed(() => {
        let tags: string[] = [];
        Object.values(items()).forEach(item => {
          if (!item['tags'] || !item['tags'].length) return;
          tags = tags.concat(item['tags']);
        });
        return tags;
      }),
    })),

    withHooks({
      onInit({ syncWithSocket }) {
        syncWithSocket();
      },
      onDestroy() {},
    })
  );
}
